/**
 * ScheduleOccupancyCard Component
 * Shows schedule occupancy rate with breakdown by professional
 */
import React from 'react';
import {
  Card, CardContent, Typography, Box, LinearProgress, Chip,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { DataTable, type DataTableColumn } from '../../../components';
import { ScheduleOccupancy } from '../../../types/dashboard';

interface ScheduleOccupancyCardProps {
  data: ScheduleOccupancy;
  loading?: boolean;
}

type ProfessionalOccupancy = NonNullable<ScheduleOccupancy['byProfessional']>[number];

const professionalColumns: DataTableColumn<ProfessionalOccupancy>[] = [
  {
    id: 'professionalName',
    label: 'Profissional',
    primary: true,
    render: (prof) => prof.professionalName,
  },
  {
    id: 'slots',
    label: 'Vagas',
    align: 'center',
    render: (prof) => `${prof.todayBooked}/${prof.todaySlots}`,
  },
  {
    id: 'occupancyRate',
    label: 'Ocupação',
    align: 'right',
    render: (prof) => (
      <Chip
        label={`${prof.occupancyRate}%`}
        size="small"
        color={prof.occupancyRate >= 90 ? 'error' : prof.occupancyRate >= 70 ? 'warning' : 'success'}
        variant="outlined"
      />
    ),
  },
];

const OccupancyBar: React.FC<{ label: string; rate: number; booked: number; total: number }> = ({
  label, rate, booked, total,
}) => {
  const color = rate >= 90 ? 'error' : rate >= 70 ? 'warning' : rate >= 40 ? 'primary' : 'info';
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {booked}/{total} vagas ({rate}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={rate}
        color={color}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
};

const ScheduleOccupancyCard: React.FC<ScheduleOccupancyCardProps> = ({ data, loading }) => {
  if (loading) return null;

  const { today, thisWeek, byProfessional } = data;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalendarMonth color="primary" />
          Ocupação da Agenda
        </Typography>

        <OccupancyBar
          label="Hoje"
          rate={today.occupancyRate}
          booked={today.bookedSlots}
          total={today.totalSlots}
        />
        <OccupancyBar
          label="Semana"
          rate={thisWeek.occupancyRate}
          booked={thisWeek.bookedSlots}
          total={thisWeek.totalSlots}
        />

        {byProfessional && byProfessional.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Por profissional (hoje)
            </Typography>
            <DataTable
              columns={professionalColumns}
              rows={byProfessional}
              getRowId={(prof) => prof.professionalName}
              emptyState={{
                title: 'Nenhum profissional com agenda hoje',
                description:
                  'A ocupação vem das agendas do dia dos profissionais das empresas do seu acesso.',
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ScheduleOccupancyCard;
